import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import Icon from "../components/Icons";
import { IMAGES, FUNDING_SUPPORT } from "../data/site";

export default function FundingSupport() {
  return (
    <Layout
      title="Funding Support"
      description="Grant applications, IPA development, business cases, funding readiness, capacity assessments, monitoring frameworks and government reporting — funding support from Ranger Base."
    >
      <PageHero
        overline="Funding Support"
        titleLines={["Turning funding", "into lasting work"]}
        subtitle="We help you secure funding and, just as importantly, get ready so that funding leads to strong, lasting results on Country."
        image={IMAGES.aerialField}
      />

      <section className="py-24 lg:py-32 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
              <div className="text-eucalyptus mb-6"><Overline>How We Help</Overline></div>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
                Practical help, from application to acquittal.
              </h2>
              <p className="mt-5 text-lg text-charcoal/70 leading-relaxed">
                Funding systems are complex. We manage that complexity so your organisation can
                focus on Country.
              </p>
              <Link to="/contact" data-testid="funding-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-eucalyptus text-warmwhite px-6 py-3 font-semibold hover:bg-eucalyptus-deep transition-colors">
                Talk about funding <ArrowUpRight size={16} />
              </Link>
            </div>

            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
              {FUNDING_SUPPORT.map((f, i) => (
                <Reveal key={f.title} delay={(i % 2) * 0.06}>
                  <div className="group h-full rounded-3xl border border-charcoal/10 bg-white p-8 hover:bg-eucalyptus transition-colors duration-300">
                    <span className="text-eucalyptus group-hover:text-sandstone text-3xl transition-colors"><Icon name={f.icon} /></span>
                    <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight text-charcoal group-hover:text-warmwhite transition-colors">{f.title}</h3>
                    <p className="mt-2 text-charcoal/65 group-hover:text-warmwhite/80 transition-colors leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-sand">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          <div className="text-eucalyptus mb-6"><Overline>Our Process</Overline></div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-charcoal leading-[0.95] max-w-2xl">A clear path to funding readiness.</h2>
          <div className="mt-14 grid md:grid-cols-4 gap-5">
            {[
              ["01", "Understand", "We learn about your organisation, your Country and your goals."],
              ["02", "Assess", "We identify strengths, gaps and the right funding opportunities."],
              ["03", "Prepare", "We build the plans, cases and systems funders want to see."],
              ["04", "Deliver", "We support delivery and reporting so results — and funding — continue."],
            ].map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.08}>
                <div className="h-full rounded-3xl bg-white border border-charcoal/10 p-7">
                  <span className="font-mono text-ochre text-lg">{n}</span>
                  <h3 className="mt-3 font-heading text-xl font-semibold tracking-tight text-charcoal">{t}</h3>
                  <p className="mt-2 text-sm text-charcoal/65 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
